import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CommonRootState } from '@common/types/common-root-state-type';
import { selectUserRole } from '@common/reducers/UserSelectors';
import { backendApi } from '@common/reducers/backend-api/backend-api';
import { useAppDispatch } from '@common/reducers/hooks/store.hook';
import PageLayout from '@/common-components/page-layout/page-layout';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExternalLink, Loader2, BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';

// Interface for bot with activity data from the backend
interface BotWithActivity {
  _id: string;
  name: string;
  description: string;
  owner: string;
  activeDeploymentId: string | null;
  lastActivity?: {
    date: string;
    eventType?: string;
  };
  [key: string]: any;
}

/**
 * BotList Component
 * 
 * Displays a list of all bots in the system with clickable links to open the bot detail view
 * in a new tab using the specified URL format. This page leverages the improved bot list endpoint
 * that includes last activity information.
 */
const BotList: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  // Get user role
  const userRole = useSelector((state: CommonRootState) => selectUserRole(state));
  const isAdmin = userRole === 'admin';
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [error, setError] = useState<string | null>(null);
  
  // State for bot data
  const [botsWithActivity, setBotsWithActivity] = useState<BotWithActivity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalBots, setTotalBots] = useState(0);

  // Calculate total pages
  const totalPages = Math.ceil(totalBots / pageSize);
  
  // Function to fetch paginated data using the getBotsWithLastActivity endpoint
  const fetchBotData = async (page: number) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Calculate skip value for pagination
      const skip = (page - 1) * pageSize;
      
      // Use the dashboard action to get bots with last activity
      const response = await dispatch(backendApi.endpoints.createEntity.initiate({
        service: 'actions',
        entity: {
          cmd: 'dashboard.getBotsWithLastActivity',
          limit: pageSize,
          skip: skip,
          sort: { updatedAt: -1 }
        }
      })).unwrap();
      
      // Update state with the data from the backend
      setBotsWithActivity(response.bots || []);
      setTotalBots(response.total || 0);
    } catch (err) {
      console.error('Error fetching bots with activity data:', err);
      setError('Failed to load bot data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch bots when page changes
  useEffect(() => {
    fetchBotData(currentPage);
  }, [currentPage, pageSize]);

  // Function to open bot detail in new tab
  const openBotDetail = (botId: string, ownerId: string) => {
    window.open(`/bot-detail-view/${botId}?supportedUserId=${ownerId}`, '_blank');
  };
  
  // Pagination handlers
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };
  
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };
  
  // Format date for last activity
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Never';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return 'Invalid date';
    }
  };

  // Check if we have data to display
  const noData = !isLoading && botsWithActivity.length === 0;

  return (
    <PageLayout title="Bot List">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <p className="text-slate-500">
            {isAdmin 
              ? "View all bots and their last activity in the system" 
              : "View and access available bots in the system"}
          </p>
          <Button 
            variant="outline" 
            className="flex items-center gap-2" 
            onClick={() => navigate('/bot-usage-dashboard')}
          >
            <BarChart3 className="h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Bots {isAdmin && "(Enhanced View)"}</CardTitle>
          </CardHeader>
          <CardContent>
            {error ? (
              <div className="text-center py-8 text-red-500">
                {error}
              </div>
            ) : isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : noData ? (
              <div className="text-center py-8 text-slate-500">
                No bots found.
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Status</TableHead>
                      {isAdmin && <TableHead>Last Activity</TableHead>}
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {botsWithActivity.map((bot) => (
                      <TableRow key={bot._id}>
                        <TableCell className="font-medium">{bot.name || 'Untitled Bot'}</TableCell>
                        <TableCell>{bot.description || 'No description'}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${bot.activeDeploymentId ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                            {bot.activeDeploymentId ? 'Deployed' : 'Draft'}
                          </span>
                        </TableCell>
                        {isAdmin && (
                          <TableCell>
                            {formatDate(bot.lastActivity?.date)}
                            {bot.lastActivity?.eventType && (
                              <div className="text-xs text-gray-500">
                                {bot.lastActivity.eventType}
                              </div>
                            )}
                          </TableCell>
                        )}
                        <TableCell className="text-right">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => bot._id && bot.owner && openBotDetail(bot._id, bot.owner)}
                            className="flex items-center gap-1"
                          >
                            <span>View</span>
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {/* Pagination controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <div className="text-sm text-slate-500">
                      Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalBots)} of {totalBots} bots
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={goToPreviousPage} 
                        disabled={currentPage === 1 || isLoading}
                        className="h-8 w-8 p-0"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="text-sm">
                        Page {currentPage} of {totalPages}
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={goToNextPage} 
                        disabled={currentPage === totalPages || isLoading}
                        className="h-8 w-8 p-0"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

export default BotList; 